// Redistribution: a group shares out a set of coins (symbolising real money) among named circles,
// one person at a time, until they agree. The server applies every move with `applyRedistribution`
// and the browser uses the same rules to show what each person may do.
//
// Coins are counted, not tracked one by one: each place holds "how many coins of each value".
// Places are the common pool (undistributed), the change reserve and every circle.

export const POOL = 'pool';
export const CHANGE = 'change';

export const REDIS_LIMITS = {
  circles: 40,
  name: 40,
  title: 80,
  kinds: 6,
  /** Coins to share out plus change coins, all values together. */
  coins: 600,
  value: 1_000_000,
} as const;

export interface RedisCircle {
  id: string;
  name: string;
}

export interface RedisCoinKind {
  value: number;
  count: number;
}

/** What the host prepares. */
export interface RedisSetup {
  title: string;
  /** People (or anything) the coins go to. They need not be in the meeting. */
  circles: RedisCircle[];
  /** Coins to share out, e.g. 10 × 10 and 10 × 5. */
  coins: RedisCoinKind[];
  /** Coins kept apart to give change, e.g. 20 × 1. Not part of what is shared out. */
  change: RedisCoinKind[];
  /** Participants of the call who take turns, in order. */
  turnOrder: string[];
}

/** place → coin value → how many. */
export type Holdings = Record<string, Record<string, number>>;

export interface RedistributionState {
  status: 'off' | 'running' | 'finished';
  title: string;
  circles: RedisCircle[];
  turnOrder: string[];
  /** Whose turn it is (participant id); null = nobody's (only the host can move). */
  turnOf: string | null;
  round: number;
  holdings: Holdings;
  /** Last thing that happened, for everyone to read. */
  last: string | null;
  startedAt: number | null;
  finishedAt: number | null;
}

export const REDIS_OFF: RedistributionState = {
  status: 'off',
  title: '',
  circles: [],
  turnOrder: [],
  turnOf: null,
  round: 0,
  holdings: {},
  last: null,
  startedAt: null,
  finishedAt: null,
};

export type RedisAction =
  /** Host: a new redistribution with every coin in the common pool. */
  | { op: 'start'; setup: RedisSetup }
  /** Host, while it runs: title, circles and turns. Coins stay where they are; a removed circle's go back to the pool. */
  | { op: 'edit'; setup: Omit<RedisSetup, 'coins' | 'change'> }
  /** One coin of `value` from one place to another. */
  | { op: 'move'; from: string; to: string; value: number }
  /** Change one coin for smaller ones from the change reserve. */
  | { op: 'break'; place: string; value: number }
  /** Join smaller coins of a place into one coin of `value` from the change reserve. */
  | { op: 'join'; place: string; value: number }
  /** End of my turn: the next person in the order who is here. */
  | { op: 'pass' }
  /** Host: give the turn to someone. */
  | { op: 'give-turn'; to: string }
  | { op: 'finish' }
  | { op: 'resume' }
  | { op: 'close' };

export interface RedisActor {
  id: string;
  name: string;
  isHost: boolean;
}

export interface RedisContext {
  /** The participant is in the call right now (turns skip anyone who is not). */
  present(participantId: string): boolean;
  /** Display name of a participant. */
  nameOf(participantId: string): string;
  now: number;
}

export type RedisResult = { ok: true; state: RedistributionState } | { ok: false; error: string };

// ───────────────────────────── reading the state ─────────────────────────────

export function countOf(h: Holdings, place: string, value: number): number {
  return h[place]?.[String(value)] ?? 0;
}

export function totalOf(h: Holdings, place: string): number {
  let sum = 0;
  for (const [v, n] of Object.entries(h[place] ?? {})) sum += Number(v) * n;
  return sum;
}

/** Coin values present anywhere, largest first. */
export function coinValues(h: Holdings): number[] {
  const values = new Set<number>();
  for (const place of Object.values(h)) for (const [v, n] of Object.entries(place)) if (n > 0) values.add(Number(v));
  return [...values].sort((a, b) => b - a);
}

/** What is being shared out: everything except the change reserve. */
export function totalToShare(h: Holdings): number {
  let sum = 0;
  for (const place of Object.keys(h)) if (place !== CHANGE) sum += totalOf(h, place);
  return sum;
}

export function placeName(s: RedistributionState, place: string): string {
  if (place === POOL) return 'el fondo común';
  if (place === CHANGE) return 'el cambio';
  return s.circles.find((c) => c.id === place)?.name ?? '—';
}

/** «de Nuci», «del fondo común». */
const fromPlace = (s: RedistributionState, place: string) => `de ${placeName(s, place)}`.replace(/^de el /, 'del ');
/** «a Nuci», «al fondo común». */
const toPlace = (s: RedistributionState, place: string) => `a ${placeName(s, place)}`.replace(/^a el /, 'al ');

/** Whether this person may move coins now. */
export function canMove(s: RedistributionState, actor: RedisActor, ctx: Pick<RedisContext, 'present'>): boolean {
  if (s.status !== 'running') return false;
  if (s.turnOf === actor.id) return true;
  // The host steps in when it is nobody's turn or the person whose turn it is is not here.
  return actor.isHost && (s.turnOf === null || !ctx.present(s.turnOf));
}

// ───────────────────────────── change ─────────────────────────────

/**
 * Coins (largest first) from `available` that add up to exactly `amount`, using only values below
 * `below`. null when it cannot be done.
 */
export function pickCoins(available: Record<string, number>, amount: number, below: number): Record<string, number> | null {
  const values = Object.keys(available)
    .map(Number)
    .filter((v) => v < below && (available[String(v)] ?? 0) > 0)
    .sort((a, b) => b - a);
  // Exact change with limited coins: small search, largest coins first. Counts are small (≤ 600 coins).
  const take: Record<string, number> = {};
  const search = (i: number, left: number): boolean => {
    if (left === 0) return true;
    if (i >= values.length) return false;
    const v = values[i];
    const max = Math.min(available[String(v)], Math.floor(left / v));
    for (let n = max; n >= 0; n--) {
      take[String(v)] = n;
      if (search(i + 1, left - n * v)) return true;
    }
    delete take[String(v)];
    return false;
  };
  if (!search(0, amount)) return null;
  for (const k of Object.keys(take)) if (!take[k]) delete take[k];
  return take;
}

// ───────────────────────────── applying an action ─────────────────────────────

const clone = (s: RedistributionState): RedistributionState => ({
  ...s,
  circles: s.circles.map((c) => ({ ...c })),
  turnOrder: [...s.turnOrder],
  holdings: Object.fromEntries(Object.entries(s.holdings).map(([p, h]) => [p, { ...h }])),
});

function add(h: Holdings, place: string, value: number, n: number): void {
  const key = String(value);
  const next = (h[place]?.[key] ?? 0) + n;
  h[place] ??= {};
  if (next > 0) h[place][key] = next;
  else delete h[place][key];
}

const fmt = (n: number) => n.toLocaleString('es');
const coinWord = (value: number, n = 1) => `${n === 1 ? 'una ficha' : `${n} fichas`} de ${fmt(value)}`;

/** Next person in the order (after `from`, wrapping) who is here; null when nobody is. */
function nextTurn(s: RedistributionState, from: string | null, ctx: RedisContext): { id: string | null; wrapped: boolean } {
  const order = s.turnOrder;
  if (!order.length) return { id: null, wrapped: false };
  const start = from ? order.indexOf(from) : -1;
  for (let step = 1; step <= order.length; step++) {
    const i = (start + step) % order.length;
    if (ctx.present(order[i])) return { id: order[i], wrapped: start >= 0 && start + step >= order.length };
  }
  return { id: null, wrapped: false };
}

function firstTurn(s: RedistributionState, ctx: RedisContext): string | null {
  return s.turnOrder.find((id) => ctx.present(id)) ?? null;
}

export function applyRedistribution(state: RedistributionState, action: RedisAction, actor: RedisActor, ctx: RedisContext): RedisResult {
  const fail = (error: string): RedisResult => ({ ok: false, error });
  const hostOnly = ['start', 'edit', 'give-turn', 'finish', 'resume', 'close'];
  if (hostOnly.includes(action.op) && !actor.isHost) return fail('Solo quien organiza la reunión puede hacer esto.');

  switch (action.op) {
    case 'start': {
      const { setup } = action;
      if (!setup.circles.length) return fail('Añade al menos un círculo.');
      const holdings: Holdings = { [POOL]: {}, [CHANGE]: {} };
      for (const k of setup.coins) add(holdings, POOL, k.value, k.count);
      for (const k of setup.change) add(holdings, CHANGE, k.value, k.count);
      if (totalOf(holdings, POOL) === 0) return fail('Indica cuántas fichas hay que repartir.');
      for (const c of setup.circles) holdings[c.id] = {};
      const s: RedistributionState = {
        status: 'running',
        title: setup.title,
        circles: setup.circles.map((c) => ({ ...c })),
        turnOrder: [...setup.turnOrder],
        turnOf: null,
        round: 1,
        holdings,
        last: null,
        startedAt: ctx.now,
        finishedAt: null,
      };
      s.turnOf = firstTurn(s, ctx);
      s.last = s.turnOf ? `Empieza la redistribución. Le toca a ${ctx.nameOf(s.turnOf)}.` : 'Empieza la redistribución.';
      return { ok: true, state: s };
    }

    case 'edit': {
      if (state.status === 'off') return fail('No hay ninguna redistribución en curso.');
      const { setup } = action;
      if (!setup.circles.length) return fail('Tiene que haber al menos un círculo.');
      const s = clone(state);
      const keep = new Set(setup.circles.map((c) => c.id));
      for (const c of state.circles) {
        if (keep.has(c.id)) continue;
        for (const [v, n] of Object.entries(s.holdings[c.id] ?? {})) add(s.holdings, POOL, Number(v), n);
        delete s.holdings[c.id];
      }
      for (const c of setup.circles) s.holdings[c.id] ??= {};
      s.title = setup.title;
      s.circles = setup.circles.map((c) => ({ ...c }));
      s.turnOrder = [...setup.turnOrder];
      if (!s.turnOf || !s.turnOrder.includes(s.turnOf)) s.turnOf = firstTurn(s, ctx);
      s.last = 'Quien organiza ha cambiado los círculos o los turnos.';
      return { ok: true, state: s };
    }

    case 'move': {
      if (!canMove(state, actor, ctx)) return fail(state.status === 'running' ? 'Espera tu turno para mover fichas.' : 'La redistribución no está en curso.');
      const { from, to, value } = action;
      if (from === to) return { ok: true, state };
      if (from === CHANGE || to === CHANGE) return fail('Las fichas del cambio solo se usan para cambiar.');
      if (!(from in state.holdings) || !(to in state.holdings)) return fail('Ese lugar ya no existe.');
      if (countOf(state.holdings, from, value) < 1) return fail(`No queda ${coinWord(value)} en ${placeName(state, from)}.`);
      const s = clone(state);
      add(s.holdings, from, value, -1);
      add(s.holdings, to, value, 1);
      s.last = `${actor.name} movió ${coinWord(value)} ${fromPlace(s, from)} ${toPlace(s, to)}.`;
      return { ok: true, state: s };
    }

    case 'break': {
      if (!canMove(state, actor, ctx)) return fail('Espera tu turno para cambiar fichas.');
      const { place, value } = action;
      if (place === CHANGE || !(place in state.holdings)) return fail('Ese lugar no existe.');
      if (countOf(state.holdings, place, value) < 1) return fail(`No hay ${coinWord(value)} en ${placeName(state, place)}.`);
      const smaller = pickCoins(state.holdings[CHANGE] ?? {}, value, value);
      if (!smaller) return fail(`No hay cambio suficiente para ${coinWord(value)}.`);
      const s = clone(state);
      add(s.holdings, place, value, -1);
      add(s.holdings, CHANGE, value, 1);
      for (const [v, n] of Object.entries(smaller)) {
        add(s.holdings, CHANGE, Number(v), -n);
        add(s.holdings, place, Number(v), n);
      }
      const parts = Object.entries(smaller).map(([v, n]) => coinWord(Number(v), n));
      s.last = `${actor.name} cambió ${coinWord(value)} por ${parts.join(' y ')} en ${placeName(s, place)}.`;
      return { ok: true, state: s };
    }

    case 'join': {
      if (!canMove(state, actor, ctx)) return fail('Espera tu turno para cambiar fichas.');
      const { place, value } = action;
      if (place === CHANGE || !(place in state.holdings)) return fail('Ese lugar no existe.');
      if (countOf(state.holdings, CHANGE, value) < 1) return fail(`No hay fichas de ${fmt(value)} en el cambio.`);
      const smaller = pickCoins(state.holdings[place] ?? {}, value, value);
      if (!smaller) return fail(`En ${placeName(state, place)} no hay fichas pequeñas que sumen ${fmt(value)}.`);
      const s = clone(state);
      for (const [v, n] of Object.entries(smaller)) {
        add(s.holdings, place, Number(v), -n);
        add(s.holdings, CHANGE, Number(v), n);
      }
      add(s.holdings, CHANGE, value, -1);
      add(s.holdings, place, value, 1);
      s.last = `${actor.name} juntó fichas pequeñas en ${coinWord(value)} en ${placeName(s, place)}.`;
      return { ok: true, state: s };
    }

    case 'pass': {
      if (state.status !== 'running') return fail('La redistribución no está en curso.');
      if (state.turnOf !== actor.id && !actor.isHost) return fail('No es tu turno.');
      return { ok: true, state: passTurn(state, ctx, actor.id === state.turnOf ? `${actor.name} pasó el turno.` : null) };
    }

    case 'give-turn': {
      if (state.status !== 'running') return fail('La redistribución no está en curso.');
      if (!ctx.present(action.to)) return fail('Esa persona no está en la reunión.');
      const s = clone(state);
      if (!s.turnOrder.includes(action.to)) s.turnOrder.push(action.to);
      s.turnOf = action.to;
      s.last = `Quien organiza le dio el turno a ${ctx.nameOf(action.to)}.`;
      return { ok: true, state: s };
    }

    case 'finish': {
      if (state.status !== 'running') return fail('La redistribución no está en curso.');
      const s = clone(state);
      s.status = 'finished';
      s.finishedAt = ctx.now;
      s.turnOf = null;
      s.last = 'Redistribución terminada.';
      return { ok: true, state: s };
    }

    case 'resume': {
      if (state.status !== 'finished') return fail('La redistribución no está terminada.');
      const s = clone(state);
      s.status = 'running';
      s.finishedAt = null;
      s.turnOf = firstTurn(s, ctx);
      s.last = 'La redistribución continúa.';
      return { ok: true, state: s };
    }

    case 'close':
      return { ok: true, state: { ...REDIS_OFF } };
  }
}

/**
 * The turn goes to the next person who is here; after the last one a new round starts.
 * Also used by the server when the person whose turn it is leaves.
 */
export function passTurn(state: RedistributionState, ctx: RedisContext, note: string | null): RedistributionState {
  const s = clone(state);
  const next = nextTurn(s, s.turnOf, ctx);
  if (next.wrapped) s.round += 1;
  s.turnOf = next.id;
  const who = next.id ? `Le toca a ${ctx.nameOf(next.id)}.` : 'No queda nadie en el orden de turnos.';
  s.last = [note, next.wrapped ? `Ronda ${s.round}.` : null, who].filter(Boolean).join(' ');
  return s;
}
